import { Token } from '@uniswap/sdk'
import { useEffect, useState } from 'react'
import { useActiveWeb3React } from './index'
import { detectTaxToken } from '../utils/dynamicTaxToken'

/**
 * Hook to detect and cache tax information for tokens used in trades
 */
export function useTaxTokenDetection(inputToken?: Token, outputToken?: Token) {
  const { library } = useActiveWeb3React()
  const [isDetecting, setIsDetecting] = useState(false)
  const [lastDetected, setLastDetected] = useState<{ input?: string; output?: string }>({})

  useEffect(() => {
    async function detectTaxes() {
      if (!library) return

      const tokensToDetect: Token[] = []

      if (inputToken && lastDetected.input !== inputToken.address) {
        tokensToDetect.push(inputToken)
      }

      if (outputToken && lastDetected.output !== outputToken.address) {
        tokensToDetect.push(outputToken)
      }

      if (tokensToDetect.length === 0) return

      setIsDetecting(true)

      try {
        // Detect taxes for all tokens in parallel
        await Promise.all(tokensToDetect.map(token => detectTaxToken(token, library)))

        // Update last detected addresses
        setLastDetected({
          input: inputToken?.address,
          output: outputToken?.address
        })
      } catch (error) {
        console.warn('Tax detection failed:', error)
      } finally {
        setIsDetecting(false)
      }
    }

    detectTaxes()
  }, [inputToken, outputToken, library, lastDetected])

  return { isDetecting }
}

/**
 * Hook to get tax information for a specific token
 */
export function useTokenTaxInfo(token?: Token) {
  const { library } = useActiveWeb3React()
  const [taxInfo, setTaxInfo] = useState<{
    isDetected: boolean
    buyTaxPercent: number
    sellTaxPercent: number
    isLoading: boolean
  }>({
    isDetected: false,
    buyTaxPercent: 0,
    sellTaxPercent: 0,
    isLoading: false
  })

  useEffect(() => {
    async function detectTax() {
      if (!token || !library) {
        setTaxInfo({
          isDetected: false,
          buyTaxPercent: 0,
          sellTaxPercent: 0,
          isLoading: false
        })
        return
      }

      setTaxInfo(prev => ({ ...prev, isLoading: true }))

      try {
        const result = await detectTaxToken(token, library)

        setTaxInfo({
          isDetected: result.isDetected,
          buyTaxPercent: parseFloat(result.buyTaxPercent.toFixed(2)),
          sellTaxPercent: parseFloat(result.sellTaxPercent.toFixed(2)),
          isLoading: false
        })
      } catch (error) {
        console.warn(`Failed to detect tax for token ${token?.symbol}:`, error)
        setTaxInfo({
          isDetected: false,
          buyTaxPercent: 0,
          sellTaxPercent: 0,
          isLoading: false
        })
      }
    }

    detectTax()
  }, [token, library])

  return taxInfo
}
